import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { useUpdateBuyOrder } from 'services/buy-process';
import { useBuyProcessForm } from '../..';
import { RootState } from '../../../../global';
import {
  onGoBackInForm,
  onGoForwardInForm,
  useSaveBuyOrderFormState,
  useSelectPeople,
} from '../../use-buy-process-form';
import { saveEditCostStatement } from '../../../admin/buy-process/buy-order-edit/buy-order-edit-redux';
import { useIsAdminOnBuyOrderEditForm } from '../../../../utils/buy-order/use-is-admin-on-buy-order-edit-form';
import {
  costStatementValidationSchema,
  getReceiverEmailsDefaultValue,
  mapPerson,
} from './cost-statement-utils';
import {
  CostStatementFormState,
  CostStatementPerson,
} from './cost-statement-types';

export const useCostStatement = () => {
  const RECEIVE_OPTION = 'receiveOption';
  const RECEIVER_EMAILS = 'receiverEmails';

  const { setRequestedStep, id } = useBuyProcessForm();
  const initialState = useSelector(
    (state: RootState) => state.buyOrderEdit[id].costStatement,
  );

  const people: CostStatementPerson[] = useSelectPeople(id).map(mapPerson);

  const formMethods = useForm<CostStatementFormState>({
    resolver: yupResolver(costStatementValidationSchema),
    defaultValues: {
      receiveOption: initialState.digitalCustomerSignature ? 'email' : 'mail',
      receiverEmails:
        initialState.receiverEmails.join(String.fromCharCode(10))
        || getReceiverEmailsDefaultValue(people),
      invoiceRecipient: initialState.invoiceRecipient,
    },
  });

  useSaveBuyOrderFormState(
    saveEditCostStatement,
    formMethods.getValues(),
    formMethods.trigger,
  );

  const receiveByEmail = formMethods.watch(RECEIVE_OPTION) === 'email';

  const dispatch = useDispatch();
  const isAdmin = useIsAdminOnBuyOrderEditForm();
  const [updateBuyOrder, updateBuyOrderMeta] = useUpdateBuyOrder(id);

  return {
    RECEIVE_OPTION,
    RECEIVER_EMAILS,
    people,
    receiveByEmail,
    formMethods,
    isUpdating: updateBuyOrderMeta.isLoading,
    onSubmit: formMethods.handleSubmit((data) => {
      dispatch(saveEditCostStatement({ ...data, id }));
      updateBuyOrder();

      if (!isAdmin) {
        setRequestedStep((s) => s + 1);
      }
    }),
    onForward: (e: React.MouseEvent<HTMLButtonElement>) =>
      onGoForwardInForm(setRequestedStep, e),
    onBack: (e: React.MouseEvent<HTMLButtonElement>) =>
      onGoBackInForm(setRequestedStep, formMethods.trigger, e),
  };
};
